import { t } from "../i18n";

// Item measurement units.
//
// An empty string / null unit means "pieces" (the default) - the row shows a
// +/- quantity stepper and displays as e.g. "2x". Any value in MEASURE_UNITS is
// a weight / volume / pack measure: the row shows the value inline (e.g.
// "500 g") with no stepper, and tapping it opens the editor.

export const MEASURE_UNITS = ["g", "kg", "ml", "L", "pack", "bunch", "dozen"];

// Picker options, pieces first. "" renders as the "pcs" pill.
export const UNIT_OPTIONS = ["", ...MEASURE_UNITS];

export function isMeasureUnit(unit) {
  return !!unit && MEASURE_UNITS.includes(unit);
}

// Trim a quantity for display: 500.0 -> "500", 0.5 -> "0.5", 1.25 -> "1.25".
export function formatQtyNumber(qty) {
  const n = Number(qty);
  if (!isFinite(n)) return "1";
  return Number.isInteger(n) ? String(n) : String(parseFloat(n.toFixed(3)));
}

// English fallbacks if a translation is missing (mirrors dietary.js pattern).
const UNIT_LABEL_DEFAULTS = { pcs: "pcs", pack: "pack", bunch: "bunch", dozen: "dozen" };

// Translated label for a unit. Metric symbols (g, kg, ml, L) are universal and
// pass through untouched; word-units (pack, bunch, dozen) and the pieces pill
// ("pcs") translate via units.* so e.g. Serbian shows "kom" / "pakovanje".
export function unitLabel(unit) {
  const key = unit === "" || unit == null ? "pcs" : unit;
  if (!(key in UNIT_LABEL_DEFAULTS)) return key;   // g/kg/ml/L stay as-is
  const tr = t(`units.${key}`);
  if (tr && tr !== `units.${key}`) return tr;
  return UNIT_LABEL_DEFAULTS[key];
}

// Full display label. Measure -> "500 g" / translated "2 pakovanje"; pieces -> "2x".
export function formatQtyUnit(qty, unit) {
  const q = qty == null ? 1 : qty;
  if (isMeasureUnit(unit)) return `${formatQtyNumber(q)} ${unitLabel(unit)}`;
  return `${formatQtyNumber(q)}x`;
}

// Label for a unit pill in the picker ("" -> translated "pcs").
export function unitPillLabel(unit) {
  return unitLabel(unit);
}

// A friendly starting quantity when an item is first switched to a unit.
// 100 g / 100 ml reads naturally; kg / L / pack / bunch / dozen start at 1.
export function defaultQtyForUnit(unit) {
  return unit === "g" || unit === "ml" ? 100 : 1;
}

// Multiplier for price totals. For pieces, price is per piece so totals use
// price * quantity. For a measure ("500 g flour" at $3), the entered price is
// the price FOR THAT AMOUNT - multiplying by 500 would inflate the list total
// 500-fold - so the factor is 1.
// True when an item's NAME already carries its own amount+unit, e.g.
// "240g Organic Firm Tofu", "500g Prawns", "80ml Olive Oil". Recipe imports
// create these with quantity=240 and NO unit, so piece-counting multiplied
// the pack price by 240. Such a line is one pack, not 240 items.
// The amount a name itself states ("500g Prawns" -> 500), or null.
export function nameMeasureAmount(item) {
  const n = (item?.name || "").trim();
  if (!n) return null;
  const m = n.match(/^(\d+(?:[.,]\d+)?)\s*(g|kg|ml|l|mg)\b/i);
  if (!m) return null;
  const amount = parseFloat(String(m[1]).replace(",", "."));
  return Number.isFinite(amount) && amount > 0 ? amount : null;
}

export function nameIsMeasure(item) {
  const amount = nameMeasureAmount(item);
  if (amount == null) return false;
  // Only when the stored quantity mirrors that amount - a genuine "2x 500g
  // packs" line (quantity 2) keeps multiplying correctly.
  return Math.abs(amount - (Number(item?.quantity) || 0)) < 0.001;
}

// Countable measure units: "2 pack" genuinely means TWO packs, so the price
// multiplies. Weight/volume units (g, kg, ml, L) do NOT — "500 g" at $3 is one
// $3 pack, not 500x. Splitting these fixed "2 pack" pricing as a single pack.
export const COUNTABLE_MEASURE_UNITS = ["pack", "bunch", "dozen"];

/**
 * A quantity as a usable number, or 1. The backend's qty_utils._as_number.
 *
 * WHY IT IS NOT JUST `Number(x) || 1` (20 September 2026)
 *
 * Number() and Python's float() disagree about what a numeric string is, and
 * running a shared corpus against both found six cases neither side's own
 * tests covered:
 *
 *     "Infinity"  here Infinity, backend 1     "0x10"   here 16, backend 1
 *     "1e999"     here Infinity, backend 1     "1_000"  here 1,  backend 1000
 *     [5]         here 5,        backend 1
 *
 * Infinity is the one that matters: `price * Infinity` is Infinity, and it
 * propagates through every total it touches. The backend's equivalent hit
 * the same class from the other direction - a fix there swapped int() for
 * float() to stop truncating, and float("NaN") does not raise where int()
 * does, so it started storing nan.
 *
 * So: a plain decimal string or a finite positive number, and nothing else.
 * A non-positive quantity is 1 rather than a negative line total - the two
 * sides used to AGREE on -3, which is the rule agreeing with itself about
 * nonsense.
 */
export function asQuantity(value) {
  if (typeof value === "string") {
    if (!/^\s*-?\d+(?:\.\d+)?\s*$/.test(value)) return 1;
    value = value.trim();
  } else if (typeof value !== "number") {
    return 1;
  }
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return 1;
  return n;
}

export function lineQty(item) {
  if (COUNTABLE_MEASURE_UNITS.includes(item?.unit)) return asQuantity(item?.quantity);
  if (isMeasureUnit(item?.unit)) return 1;
  const amount = nameMeasureAmount(item);
  if (amount != null) {
    const q = asQuantity(item?.quantity);
    // "500g …" qty 500 = one pack. Not for a small size (V19.09, live test
    // 19.08 item 1): "2L milk" x2 is two bottles, and was priced as one.
    // A fraction ("1.5kg", "0,5l") is never a count, so it still mirrors.
    if ((amount >= 10 || !Number.isInteger(amount)) && Math.abs(amount - q) < 0.001) return 1;
    // Meal-plan day merging SUMS grams-quantities: "500g Prawns" planned on
    // 3 days stored quantity 1500 - which then multiplied the pack price
    // 1500-fold (the A$25,433.50 Euro Style bug). A quantity at or above the
    // name's own amount is grams, not a piece count, so it prices as
    // quantity/amount packs (1500/500 = 3 packs). amount >= 10 keeps tiny
    // leading numbers ("2g saffron" bought 4x) counting as pieces.
    if (amount >= 10 && q >= amount) return q / amount;
  }
  return asQuantity(item?.quantity);
}
